import type { RemoveIndexSignature } from 'src/types/removeIndexSignature';

import { ComposerTranslation, LocaleMessageValue, VueMessageType } from 'vue-i18n';
import { RouteLocationNormalizedLoaded, Router } from 'vue-router';

import { AxiosStatic } from 'axios';

import { usePhoneCallStore } from 'src/stores/phoneCall';
import { useUserStore } from 'src/stores/user';

import { DirectPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/DirectPhoneCallGatewayStrategy';
import { DirectPhoneCallGatewayWithHangupFunctionalityStrategy } from 'src/components/phoneCall/phoneCallGateway/DirectPhoneCallGatewayWithHangupFunctionalityStrategy';
import { PhoneCallGateway } from 'src/components/phoneCall/phoneCallGateway/PhoneCallGateway';
import { PollingPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/PollingPhoneCallGatewayStrategy';
import { CallToDirectPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/callto/CallToDirectPhoneCallGatewayStrategy';
import { CallToPollingPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/callto/CallToPollingPhoneCallGatewayStrategy';
import { GenericPostDirectPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/genericPostRequest/GenericPostDirectPhoneCallGatewayStrategy';
import { GenericPostDirectPhoneCallGatewayWithHangupFunctionalityStrategy } from 'src/components/phoneCall/phoneCallGateway/genericPostRequest/GenericPostDirectPhoneCallGatewayWithHangupFunctionalityStrategy';
import { GenericPostPollingPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/genericPostRequest/GenericPostPollingPhoneCallGatewayStrategy';
import { GenericPostPollingPhoneCallGatewayWithHangupFunctionalityStrategy } from 'src/components/phoneCall/phoneCallGateway/genericPostRequest/GenericPostPollingPhoneCallGatewayWithHangupFunctionalityStrategy';
import { SipgateDirectPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/sipgate/SipgateDirectPhoneCallGatewayStrategy';
import { StarfacePollingPhoneCallGatewayWithHangupFunctionalityStrategy } from 'src/components/phoneCall/phoneCallGateway/starface/StarfacePollingPhoneCallGatewayWithHangupFunctionalityStrategy';
import { TapiMasterExternDirectPhoneCallGatewayWithHangupFunctionalityStrategy } from 'src/components/phoneCall/phoneCallGateway/tapiMasterExtern/TapiMasterExternDirectPhoneCallGatewayWithHangupFunctionalityStrategy';
import { TelDirectPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/tel/TelDirectPhoneCallGatewayStrategy';

import { OutgoingCallConfigurationForTheUser } from 'src/models/phoneCall';

import { CommandBus } from 'src/util/command/CommandBus';
import { EventBus } from 'src/util/eventBus/EventBus';
import { isRunningInNativeCapacitorApp } from 'src/util/platform/browserInteractionCapabilities';
import { QueryStore } from 'src/util/query/QueryStore';

type PhoneCallStore = ReturnType<typeof usePhoneCallStore>;
type PhoneCallGatewayStrategy =
	| DirectPhoneCallGatewayStrategy
	| DirectPhoneCallGatewayWithHangupFunctionalityStrategy
	| PollingPhoneCallGatewayStrategy;

export class PhoneCallGatewayFactory {
	private authenticatedUserId: number | undefined;
	private readonly axios: AxiosStatic;
	private readonly commandBus: CommandBus;
	private outGoingCallConfigurationForTheUser: OutgoingCallConfigurationForTheUser | null = null;
	private readonly queryStore: QueryStore;
	private readonly detailsContext: unknown;
	private readonly phoneCallStore: PhoneCallStore;
	private readonly router: Router;
	private readonly route: RouteLocationNormalizedLoaded;

	private readonly eventBus: EventBus;
	private readonly shouldUseTheSplitView: boolean;
	private readonly t: ComposerTranslation<
		{
			[p: string]: LocaleMessageValue<VueMessageType>;
		},
		string,
		RemoveIndexSignature<{
			[p: string]: LocaleMessageValue<VueMessageType>;
		}>,
		never,
		string,
		string
	>;
	private strategy: PhoneCallGatewayStrategy | undefined;

	constructor(
		axios: AxiosStatic,
		queryStore: QueryStore,
		commandBus: CommandBus,
		detailsContext: unknown,
		phoneCallStore: PhoneCallStore,
		router: Router,
		route: RouteLocationNormalizedLoaded,
		eventBus: EventBus,
		shouldUseTheSplitView: boolean,
		t: ComposerTranslation<
			{
				[p: string]: LocaleMessageValue<VueMessageType>;
			},
			string,
			RemoveIndexSignature<{
				[p: string]: LocaleMessageValue<VueMessageType>;
			}>,
			never,
			string,
			string
		>
	) {
		this.axios = axios;
		this.queryStore = queryStore;
		this.commandBus = commandBus;
		this.detailsContext = detailsContext;
		this.phoneCallStore = phoneCallStore;
		this.router = router;
		this.route = route;
		this.eventBus = eventBus;
		this.shouldUseTheSplitView = shouldUseTheSplitView;
		this.t = t;
	}

	public async initialize() {
		await this.loadAuthenticatedUserInformation();
		await this.loadOutgoingCallConfiguration();
		this.clearActiveCall();
	}

	public async createPhoneCallGateway(): Promise<PhoneCallGateway | null> {
		await this.initialize();

		if (this.authenticatedUserId === undefined) {
			return null;
		}

		this.strategy = this.createStrategy(this.authenticatedUserId);

		if (this.strategy === undefined) {
			return null;
		}

		return new PhoneCallGateway(
			this.commandBus,
			this.detailsContext,
			this.phoneCallStore,
			this.authenticatedUserId,
			this.router,
			this.route,
			this.eventBus,
			this.shouldUseTheSplitView,
			this.t,
			this.strategy
		);
	}

	private createStrategy(authenticatedUserId: number): PhoneCallGatewayStrategy | undefined {
		const configuration = this.outGoingCallConfigurationForTheUser;
		const httpProviderStrategy =
			configuration === null ? undefined : this.createHttpProviderStrategy(configuration, authenticatedUserId);

		if (httpProviderStrategy !== undefined) {
			return httpProviderStrategy;
		}

		if (isRunningInNativeCapacitorApp()) {
			return new TelDirectPhoneCallGatewayStrategy(this.commandBus, authenticatedUserId);
		}

		if (configuration?.location === 'asterisk') {
			return configuration.call_polling === 'on'
				? new CallToPollingPhoneCallGatewayStrategy(this.phoneCallStore, this.queryStore)
				: new CallToDirectPhoneCallGatewayStrategy(this.commandBus, authenticatedUserId);
		}

		return undefined;
	}

	private createHttpProviderStrategy(
		configuration: OutgoingCallConfigurationForTheUser,
		authenticatedUserId: number
	): PhoneCallGatewayStrategy | undefined {
		switch (configuration.location) {
			case 'tapimasterExtern':
				return new TapiMasterExternDirectPhoneCallGatewayWithHangupFunctionalityStrategy(
					this.axios,
					this.phoneCallStore,
					this.commandBus,
					authenticatedUserId,
					configuration
				);
			case 'sipgateIO':
				return new SipgateDirectPhoneCallGatewayStrategy(
					this.axios,
					this.phoneCallStore,
					this.commandBus,
					authenticatedUserId,
					configuration
				);
			case 'starface':
				return new StarfacePollingPhoneCallGatewayWithHangupFunctionalityStrategy(
					this.axios,
					this.phoneCallStore,
					this.queryStore,
					this.commandBus,
					authenticatedUserId,
					configuration
				);
			case 'curlPost':
				return this.createCurlPostStrategy(configuration, authenticatedUserId);
			case 'asterisk':
				return undefined;
		}
	}

	private createCurlPostStrategy(
		configuration: OutgoingCallConfigurationForTheUser,
		authenticatedUserId: number
	): PhoneCallGatewayStrategy | undefined {
		if (!configuration.url_init) {
			return undefined;
		}

		const hasHangup = !!configuration.url_hangup;

		if (configuration.call_polling === 'on') {
			return hasHangup
				? new GenericPostPollingPhoneCallGatewayWithHangupFunctionalityStrategy(
						this.axios,
						this.phoneCallStore,
						this.queryStore,
						this.commandBus,
						authenticatedUserId,
						configuration
					)
				: new GenericPostPollingPhoneCallGatewayStrategy(
						this.axios,
						this.phoneCallStore,
						this.queryStore,
						configuration
					);
		}

		return hasHangup
			? new GenericPostDirectPhoneCallGatewayWithHangupFunctionalityStrategy(
					this.axios,
					this.phoneCallStore,
					this.commandBus,
					authenticatedUserId,
					configuration
				)
			: new GenericPostDirectPhoneCallGatewayStrategy(
					this.axios,
					this.commandBus,
					authenticatedUserId,
					configuration
				);
	}

	private async loadOutgoingCallConfiguration() {
		await this.phoneCallStore.fetchTheOutgoingCallConfigurationForTheUser({
			queryStore: this.queryStore,
		});

		this.outGoingCallConfigurationForTheUser = this.phoneCallStore.outgoingCallConfigurationForTheUser;
	}

	private async loadAuthenticatedUserInformation() {
		const userStore = useUserStore();
		await userStore.fetchAuthenticatedUserInformation({
			queryStore: this.queryStore,
		});
		const authenticatedUserInformation = userStore.authenticatedUserInformation;

		this.authenticatedUserId = authenticatedUserInformation?.id;
	}

	protected clearActiveCall() {
		this.phoneCallStore.setOutgoingCallWithHangupFunctionalityCallId({
			callId: null,
		});

		this.phoneCallStore.setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp({
			nextOutgoingCallIdForACalledNumberAfterAGivenTimestamp: null,
		});
	}
}
